import { createContext, useContext, useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'

const Ctx = createContext(null)

const MENU_W = 192

export function ContextMenuProvider({ children }) {
  const [menu, setMenu] = useState(null)

  useEffect(() => {
    if (!menu) return
    const close = () => setMenu(null)
    // Defer so the triggering click doesn't immediately close
    const timer = setTimeout(() => {
      window.addEventListener('click', close)
      window.addEventListener('contextmenu', close)
    }, 0)
    const onKey = (e) => { if (e.key === 'Escape') setMenu(null) }
    window.addEventListener('keydown', onKey)
    return () => {
      clearTimeout(timer)
      window.removeEventListener('click', close)
      window.removeEventListener('contextmenu', close)
      window.removeEventListener('keydown', onKey)
    }
  }, [menu])

  return (
    <Ctx.Provider value={setMenu}>
      {children}
      <AnimatePresence>
        {menu && <ContextMenuFloating key="ctx" menu={menu} onClose={() => setMenu(null)} />}
      </AnimatePresence>
    </Ctx.Provider>
  )
}

export function useContextMenu() {
  const setMenu = useContext(Ctx)
  if (!setMenu) return () => {}
  return function open(e, items) {
    e.preventDefault()
    e.stopPropagation()
    // Estimate menu height to clamp position
    const h = items.reduce((acc, i) => acc + (i.separator ? 9 : 32), 8)
    setMenu({
      x: Math.min(e.clientX, window.innerWidth  - MENU_W - 8),
      y: Math.min(e.clientY, window.innerHeight - h - 8),
      items,
    })
  }
}

function ContextMenuFloating({ menu, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ duration: 0.1, ease: [0.23, 1, 0.32, 1] }}
      role="menu"
      style={{ top: menu.y, left: menu.x, transformOrigin: 'top left', width: MENU_W }}
      className="fixed z-[9999] bg-black/70 backdrop-blur-xl border border-white/[0.08] rounded-xl shadow-2xl shadow-black/50 py-1 overflow-hidden"
      onClick={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
    >
      {menu.items.map((item, i) =>
        item.separator ? (
          <div key={i} className="my-1 h-px bg-border/60 mx-2" />
        ) : (
          <button
            key={i}
            role="menuitem"
            onClick={() => { item.onClick?.(); onClose() }}
            className={cn(
              'w-full flex items-center gap-2.5 px-3 h-8 text-[13px] transition-colors',
              item.danger
                ? 'text-red-400 hover:bg-red-500/10'
                : 'text-text-muted hover:text-text hover:bg-white/[0.06]'
            )}
          >
            {item.icon && <item.icon size={13} className="flex-shrink-0 opacity-70" />}
            <span className="flex-1 text-left">{item.label}</span>
            {item.shortcut && <span className="ml-2 text-[11px] text-text-faint">{item.shortcut}</span>}
          </button>
        )
      )}
    </motion.div>
  )
}
